import React from "react";

import clsx from "clsx";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { type CategoricalBreakdownConfig } from "@north-beam/nb-common";

import { BreakdownSelector } from "../../shared";
import { type TopLevelBreakdown } from "../../types";

interface Props {
  breakdownConfigs: CategoricalBreakdownConfig[];
  breakdown: TopLevelBreakdown;
  position: number;
  showPosition: boolean;
  onChange(value: TopLevelBreakdown): void;
  onDelete(): void;
}

export const BreakdownButtonActive = ({
  breakdownConfigs,
  breakdown,
  position,
  showPosition,
  onChange,
  onDelete,
}: Props) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
  } = useSortable({
    id: breakdown.key,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      className={clsx("text-sm h-8 rounded flex items-stretch bg-white", {
        "z-[999] shadow-sm": isDragging,
      })}
      style={style}
    >
      <div
        className={clsx(
          "flex border border-r-0 rounded-l bg-white",

          {
            "border-gray": !isDragging,
            border: !isDragging,
            "border-cosmic+1": isDragging,
            group: !isSorting,
          },
        )}
      >
        {showPosition && (
          <div
            ref={setActivatorNodeRef}
            className={clsx(
              "flex items-center justify-center w-7 hover:bg-cosmic-4 rounded-l",
              "cursor-grab",
              { "cursor-grabbing": isDragging },
            )}
            {...attributes}
            {...listeners}
          >
            <span
              className={clsx(
                "w-4 h-4 bg-cosmic text-xxxs text-white rounded-full",
                "flex items-center justify-center group-hover:hidden",
                { hidden: isDragging },
              )}
            >
              {!isSorting && position}
            </span>
            <i
              className={clsx(
                "fa-regular fa-sharp fa-grip-dots-vertical group-hover:block",
                { hidden: !isDragging, block: isDragging },
              )}
            ></i>
          </div>
        )}
        <BreakdownSelector
          mode="breakdowns"
          breakdownConfigs={breakdownConfigs}
          selectedBreakdowns={[breakdown]}
          buttonClassName={clsx(
            "max-w-[106px] h-full px-3 flex items-center hover:bg-cosmic-4",
            {
              "rounded-l": !showPosition,
              "pl-1.5": showPosition,
            },
          )}
          buttonActiveClassName={clsx(
            "max-w-[106px] h-full px-3 flex items-center hover:bg-cosmic-4",
            {
              "rounded-l": !showPosition,
              "pl-1.5": showPosition,
            },
          )}
          text={breakdownConfigs.find((e) => e.key === breakdown.key)?.name}
          onSelectionChange={(values) => onChange(values[0])}
        />
      </div>
      <button
        className={clsx(
          "w-8 border rounded-r text-cosmic",
          "hover:bg-mars-2 hover:text-mars hover:border-mars",
          {
            border: !isDragging,
            "border-l border-y-cosmic+1 border-r-cosmic+1": isDragging,
          },
        )}
        onClick={onDelete}
      >
        <i className={clsx("fa-regular fa-sharp fa-times-circle")}></i>
      </button>
    </div>
  );
};
